import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FaIcons from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';

const VideoComparisonTable = () => {
  const comparisonData = [
    {
      metric: "Traffic Longevity & Growth",
      video: "Lifetime (compounding assets)",
      blog: "Medium (decays over time)",
      ads: "Immediate stop"
    },
    {
      metric: "Ownership",
      video: "Owned digital asset (ranks and compounds)",
      blog: "Owned, but slow to build authority",
      ads: "Not owned (traffic stops when spend ends)"
    },
    {
      metric: "Trust & Authority Signal",
      video: "High (face, voice, brand presence)",
      blog: "Medium (text-only credibility)",
      ads: "Low (ad skepticism)"
    },
    {
      metric: "SERP Visibility Type",
      video: "Video carousel, thumbnails, chapters",
      blog: "Standard blue links",
      ads: "Marked as \"Ad\""
    },
    {
      metric: "Competitive Barrier",
      video: "High (channels, assets, authority)",
      blog: "Medium (easy to replicate)",
      ads: "Low (anyone can outbid)"
    },
    {
      metric: "Platform Reach",
      video: "Google, YouTube, Suggested, Discover",
      blog: "Google only",
      ads: "Single platform per campaign"
    }
  ];

  return (
    <section className="relative py-24 bg-[#0B0F19] overflow-hidden border-b border-white/5">
      {/* Subtle background glows */}
      <div className="absolute top-1/2 right-0 w-[800px] h-[500px] bg-purple-900/10 blur-[150px] rounded-full pointer-events-none translate-x-1/3 -translate-y-1/2"></div>
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-blue-900/10 blur-[150px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2"></div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-white tracking-tight"
          >
            Why Video Beats Blogs and Ads
          </motion.h2>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="w-full overflow-x-auto pb-6"
        >
          <div className="min-w-[900px] rounded-2xl border border-white/10 bg-[#12151e] overflow-hidden shadow-2xl">
            {/* Table Header */}
            <div className="grid grid-cols-[1.5fr_2fr_1.5fr_1.5fr] p-6 border-b border-white/10 bg-white/[0.02]">
              <div className="font-bold text-white text-sm">Metric</div>
              <div className="font-bold text-white text-sm">Video Assets (PRBoost)</div>
              <div className="font-bold text-white text-sm">Traditional Blogs</div>
              <div className="font-bold text-white text-sm">Paid Ads (PPC)</div>
            </div>

            {/* Table Body */}
            <div>
              {comparisonData.map((row, idx) => (
                <div 
                  key={idx} 
                  className={`grid grid-cols-[1.5fr_2fr_1.5fr_1.5fr] p-6 items-center transition-colors hover:bg-white/[0.02] ${
                    idx !== comparisonData.length - 1 ? 'border-b border-white/5' : ''
                  }`}
                >
                  {/* Metric Column */}
                  <div className="font-bold text-white text-[15px] pr-4">
                    {row.metric}
                  </div>
                  
                  {/* Video Assets Column */}
                  <div className="flex items-start gap-3 pr-4">
                    <SafeIcon icon={FaIcons.FaCheckCircle} className="text-[#22c55e] text-lg flex-shrink-0 mt-0.5" />
                    <span className="font-bold text-white text-[15px] leading-snug">
                      {row.video}
                    </span>
                  </div>

                  {/* Traditional Blogs Column */}
                  <div className="flex items-start gap-3 pr-4">
                    <SafeIcon icon={FiIcons.FiX} className="text-[#ef4444] text-xl flex-shrink-0 stroke-[3]" />
                    <span className="text-slate-400 text-[15px] font-medium leading-snug">
                      {row.blog}
                    </span>
                  </div>

                  {/* Paid Ads Column */}
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiX} className="text-[#ef4444] text-xl flex-shrink-0 stroke-[3]" />
                    <span className="text-slate-400 text-[15px] font-medium leading-snug">
                      {row.ads}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Footer Quote */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-slate-400 text-lg md:text-xl italic font-medium">
            "Video SEO assets are not a marketing expense. They are digital real estate that compounds over time."
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoComparisonTable;